import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { analytics } from '../../services/analytics/analytics';
import { AnalyticsEvent } from '../../services/analytics/events';

const { width } = Dimensions.get('window');

interface TutorialStep {
  id: number;
  message: string;
  buttonText: string;
  icon?: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    message: "Hi! I'm Orion, your personal guide for FinalKey. 👋\n\nBefore we get started, I want to take just a minute to explain how FinalKey works and how it can help you and your loved ones.\n\nFinalKey is like a digital safety deposit box for your life's important information - medical details, financial accounts, passwords, end-of-life wishes, and more.\n\nThe goal? To make sure the people you trust can access what they need, when they need it - without the stress and confusion that often comes during difficult times.",
    buttonText: "Tell me more",
    icon: "👋",
  },
  {
    id: 2,
    message: "Everything you store in FinalKey is protected in what we call 'The Vault' 🔐\n\nThink of it like a bank vault, but for your personal information:\n\n• All sensitive data is encrypted with industry-standard AES-256\n• When you set a Recovery Code, your vault can only be unlocked with that code — we cannot recover it for you\n• Only you control who can access what, and when\n\nYou can store things like:\n• Medical records and medications\n• Bank accounts and credit cards\n• Insurance policies\n• Passwords and login credentials\n• Funeral wishes and advance directives\n\nAnd you decide who gets access to what, and when.",
    buttonText: "Who has access?",
    icon: "🔐",
  },
  {
    id: 3,
    message: "FinalKey has three important roles. Let me explain each one:\n\n👤 You - The Primary User\nThat's you! You create and manage all your information. You decide what to lock and who can access it.\n\n🔑 Keyholder\nThis is a person or people you trust to confirm when you've passed away. They don't automatically get access to your information - you choose what information they can access. Their role is to verify your death so your Successor can then receive access to everything you've stored.\n\n🎯 Successor\nThis person (or people) will receive access to all your information when you have an end of life event. You can also choose what information they can access before an end of life event. They'll handle your affairs and execute your wishes.\n\nYou'll choose these people later - and you can change them anytime.",
    buttonText: "How do I control access?",
    icon: "👥",
  },
  {
    id: 4,
    message: "Here's one of my favorite features: the Lock/Unlock system 🔒\n\nFor sensitive information (like bank accounts or medical details), you get to choose:\n\n🔒 Locked Mode (Default)\n• Information is encrypted and hidden\n• Successors see that the task exists, but can't see details\n• Perfect for information you want to share only after you're gone\n\n🔓 Unlocked Mode\n• Information is visible to whoever you select to have access to that information. That can be both Keyholders and Successors.\n\nYou'll see this lock/unlock option throughout the app. Don't worry - we'll guide you on what makes sense to lock or unlock for each type of information.",
    buttonText: "Got it!",
    icon: "🔒",
  },
  {
    id: 5,
    message: "Perfect! Now you understand the basics. Here's what we'll do next:\n\nI'm going to ask you 5 quick questions about your life situation - things like:\n• Your family structure\n• Health considerations\n• Financial situation\n• Planning priorities\n\nBased on your answers, I'll create a personalized task list - showing you exactly what to complete first, second, third, etc.\n\nNo pressure to finish everything today! You can always pause and come back. We're building this together, at your pace.\n\nReady to get started?",
    buttonText: "Let's do this!",
    icon: "🎯",
  },
];

export const AIOnboardingTutorialScreen = ({ navigation, route }: any) => {
  const mode = route?.params?.mode || 'quick_start';
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState<TutorialStep[]>([]);
  const scrollViewRef = useRef<ScrollView>(null);
  const currentStepRef = useRef(0);

  useEffect(() => {
    // Show first message immediately
    setMessages([tutorialSteps[0]]);
    analytics.track(AnalyticsEvent.AiOnboardingTutorialShown, { mode });
  }, []);

  useEffect(() => {
    // Scroll to bottom when new message appears
    if (messages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages]);

  const showNextMessage = (stepIndex: number) => {
    if (stepIndex < tutorialSteps.length) {
      setMessages(prev => [...prev, tutorialSteps[stepIndex]]);
    }
  };

  const handleNext = () => {
    const nextStep = currentStepRef.current + 1;

    if (nextStep < tutorialSteps.length) {
      currentStepRef.current = nextStep;
      setCurrentStep(nextStep);
      setTimeout(() => {
        showNextMessage(nextStep);
      }, 300);
    } else {
      // Tutorial complete, navigate to questions
      analytics.track(AnalyticsEvent.AiOnboardingChatStarted, { mode, via: 'tutorial_complete' });
      navigation.replace('AIOnboardingChat', { mode });
    }
  };

  const handleSkip = () => {
    analytics.track(AnalyticsEvent.AiOnboardingChatStarted, { mode, via: 'tutorial_skipped' });
    navigation.replace('AIOnboardingChat', { mode });
  };

  const currentMessage = messages[messages.length - 1];
  const progressPercentage = ((currentStep + 1) / tutorialSteps.length) * 100;

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Getting Started</Text>
        <TouchableOpacity onPress={handleSkip}>
          <Text style={styles.skipText}>Skip</Text>
        </TouchableOpacity>
      </View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              { width: `${progressPercentage}%` },
            ]}
          />
        </View>
        <Text style={styles.progressText}>
          {currentStep + 1} of {tutorialSteps.length}
        </Text>
      </View>

      {/* Messages */}
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.messagesContainer}
        showsVerticalScrollIndicator={false}
      >
        {messages.map((step, index) => (
          <View
            key={`step-${step.id}-${index}`}
            style={styles.messageWrapper}
          >
            <View style={styles.messageContainer}>
              <View style={styles.avatarContainer}>
                <Image
                  source={require('../../assets/icons/chat.png')}
                  style={styles.avatarImage}
                />
              </View>
              <View style={styles.messageBubble}>
                <Text style={styles.messageText}>{step.message}</Text>
              </View>
            </View>
          </View>
        ))}
      </ScrollView>

      {/* Action Button */}
      {currentMessage && (
        <View style={styles.actionContainer}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleNext}
            activeOpacity={0.8}
          >
            <Text style={styles.actionButtonText}>
              {currentMessage.buttonText}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  skipText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  progressContainer: {
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  progressBar: {
    height: s(4),
    backgroundColor: '#E0E0E0',
    borderRadius: ms(2),
    marginBottom: ms(8),
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#29BFC0',
    borderRadius: ms(2),
  },
  progressText: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  messagesContainer: {
    paddingHorizontal: ms(16),
    paddingVertical: ms(24),
    paddingBottom: ms(120),
  },
  messageWrapper: {
    marginBottom: ms(24),
  },
  messageContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatarContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(12),
  },
  avatarImage: {
    width: s(24),
    height: s(24),
  },
  messageBubble: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    borderTopLeftRadius: ms(4),
    padding: ms(16),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  messageText: {
    fontSize: ms(16, 0.3),
    lineHeight: ms(24, 0.3),
    color: '#1C2C4F',
  },
  actionContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: ms(24),
    paddingVertical: ms(20),
    paddingBottom: ms(32),
    backgroundColor: '#FFFFFF',
    borderTopWidth: ms(1),
    borderTopColor: '#E0E0E0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 5,
  },
  actionButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(16),
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  actionButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
